import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'

import { apiUrl } from '../utils/apiClient'

export interface PushNotificationItem {
  id: number
  title: string
  description: string
  colour: string
  link?: string
  link_label?: string // optional button text when link is set (e.g. "Add payment method")
}

interface PushNotificationContextType {
  notifications: PushNotificationItem[]
  acknowledge: (id: number) => Promise<void>
}

const PushNotificationContext = createContext<PushNotificationContextType | undefined>(undefined)

export const usePushNotifications = () => {
  const context = useContext(PushNotificationContext)
  if (context === undefined) {
    throw new Error('usePushNotifications must be used within a PushNotificationProvider')
  }
  return context
}

interface PushNotificationProviderProps {
  children: React.ReactNode
  isAuthenticated: boolean
  getToken: () => string | null
}

export const PushNotificationProvider: React.FC<PushNotificationProviderProps> = ({
  children,
  isAuthenticated,
  getToken,
}) => {
  const [notifications, setNotifications] = useState<PushNotificationItem[]>([])
  const abortRef = useRef<AbortController | null>(null)

  const acknowledge = useCallback(async (id: number) => {
    const token = getToken()
    if (!token) return
    try {
      const res = await fetch(apiUrl(`/api/notifications/${id}/ack`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) {
        setNotifications(prev => prev.filter(n => n.id !== id))
      }
    } catch {
      setNotifications(prev => prev.filter(n => n.id !== id))
    }
  }, [getToken])

  useEffect(() => {
    if (!isAuthenticated) {
      setNotifications([])
      if (abortRef.current) {
        abortRef.current.abort()
        abortRef.current = null
      }
      return
    }

    const token = getToken()
    if (!token) return

    const ac = new AbortController()
    abortRef.current = ac

    let closed = false
    const run = async () => {
      try {
        const res = await fetch(apiUrl(`/api/notifications/stream`), {
          headers: { Authorization: `Bearer ${token}` },
          signal: ac.signal,
        })
        if (!res.ok || !res.body) return
        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        while (!closed && !ac.signal.aborted) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''
          let event = ''
          let data: string[] = []
          for (const line of lines) {
            if (line.startsWith('event:')) {
              event = line.slice(6).trim()
            } else if (line.startsWith('data:')) {
              data.push(line.slice(5).trim())
            } else if (line === '' && (event || data.length)) {
              if (event === 'notification' && data.length > 0) {
                try {
                  const payload = JSON.parse(data.join('\n')) as PushNotificationItem
                  setNotifications(prev => {
                    if (prev.some(n => n.id === payload.id)) return prev
                    return [...prev, payload]
                  })
                } catch {
                  // ignore parse errors
                }
              }
              event = ''
              data = []
            }
          }
        }
      } catch (e) {
        if ((e as Error).name !== 'AbortError') {
          // Reconnect after a delay when connection drops
          setTimeout(run, 5000)
        }
      }
    }
    run()
    return () => {
      closed = true
      ac.abort()
      abortRef.current = null
    }
  }, [isAuthenticated, getToken])

  return (
    <PushNotificationContext.Provider value={{ notifications, acknowledge }}>
      {children}
    </PushNotificationContext.Provider>
  )
}
